import React, { Component } from "react";
import simulationandtrain from "../../../assets/create_campaign/simulationandtrain.png";
import "./steps.css";
import { attackVectorUsesLandingPage } from "../../ai-components/campaign-template-utils";

const formatCampaignExpiryLabel = (campaignDetails) => {
	const expiryDays = campaignDetails?.expiryDays;
	const customExpiryDate = campaignDetails?.customExpiryDate;

	if (expiryDays === 7 || expiryDays === "7") {
		return "7 Days";
	}
	if (expiryDays === 15 || expiryDays === "15") {
		return "15 Days";
	}
	if (expiryDays === "custom") {
		const raw = customExpiryDate != null ? String(customExpiryDate).trim() : "";
		if (!raw) {
			return "Custom";
		}
		const parsed = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
		if (!isNaN(parsed.getTime())) {
			const pad = (n) => String(n).padStart(2, "0");
			const monthName = parsed.toLocaleString("en-US", { month: "long" });
			const day = pad(parsed.getDate());
			const year = parsed.getFullYear();
			let hours = parsed.getHours();
			const ampm = hours >= 12 ? "PM" : "AM";
			hours = hours % 12 || 12;
			return `${day} ${monthName} ${year} | ${hours}:${pad(parsed.getMinutes())} ${ampm}`;
		}
		return raw;
	}

	return "7 Days";
};

export default class PreviewStep extends Component {
	render() {
		const { allData } = this.props;
		const {
			simulationType,
			attackType,
			campaignDetails,
			templateId,
			selectedTemplates,
			training,
			followUpMode,
			conditions,
			totalUsers
		} = allData || {};
		const trainingCourses = training?.selectedCourses || [];
		const trainingCourseNames = training?.courseName || {};
		const isFollowUp = followUpMode === true || (conditions && conditions.length > 0);

		// Get target groups from campaignDetails
		const targetGroups = campaignDetails?.targetGroups || campaignDetails?.selectedOptions || [];
		const campaignName = campaignDetails?.campaignName || "New Campaign";
		const campaignExpiryLabel = formatCampaignExpiryLabel(campaignDetails);
		const instantAwareness =
			simulationType === "Simulation Attack" ? false : campaignDetails?.instantAwareness === true;
		const isRepliedCampaign = campaignDetails?.isRepliedCampaign === true;
		const credentialHarvestingEnabled = campaignDetails?.credentialHarvesting !== false;
		const templatesWithKnownLandingState = (Array.isArray(selectedTemplates) ? selectedTemplates : [])
			.filter((t) => t && t.landingUrl !== undefined);
		/* Only attack vectors that actually route through a landing page lose credential
		   harvesting when none is configured; inbound Vishing / AI WhatsApp harvest inside
		   the call or chat, so a missing landing page is irrelevant there. */
		const usesLandingPage = attackVectorUsesLandingPage({
			attackType,
			targetInteractionType: campaignDetails?.targetInteractionType,
			isAIbased: campaignDetails?.isAIbased
		});
		const noLandingPageAnywhere = usesLandingPage &&
			templatesWithKnownLandingState.length > 0 &&
			templatesWithKnownLandingState.every((t) => !t.landingUrl);
		const credentialHarvestingEffective = credentialHarvestingEnabled && !noLandingPageAnywhere;
		const targetInteractionType = (campaignDetails?.targetInteractionType || "").trim();
		const isAIbased =
			campaignDetails?.isAIbased === true ||
			campaignDetails?.isAIbased === 1 ||
			String(campaignDetails?.isAIbased || "").toLowerCase() === "true";

		// Format attack type for display 
		const formatAttackType = (type) => {
			if (!type) return "Not selected";
			if (type === "Phishing") return "Phishing";
			return type;
		};

		// Display logic for groups 
		const maxVisibleGroups = 4;
		const visibleGroups = targetGroups.slice(0, maxVisibleGroups);
		const remainingCount = Math.max(0, targetGroups.length - maxVisibleGroups);

		// Templates: trust selectedTemplates when parent synced it. Empty array = no selection (do not fall back to stale templateId).
		const templatesToShow = Array.isArray(selectedTemplates)
			? selectedTemplates
			: templateId
				? [{ id: templateId, name: "Template Selected" }]
				: [];
		const followUpConditionText = Array.isArray(conditions)
			? conditions
				.map((item, index) => {
					const label = typeof item === "object" ? (item.event || item.label) : item;
					const operator = typeof item === "object" ? item.operator : null;
					if (!label) return "";
					return index > 0 && operator ? `${operator} ${label}` : label;
				})
				.filter(Boolean)
				.join(" ")
			: "";

		return (
			<div className="step-content-wrapper">
				<div className="step-header">
					<h4>Preview</h4>
					<p>Review the campaign setup and template before launching the simulation.</p>
				</div>
				<div className="preview-content">
					<div className="preview-section">
						<div className="preview-settings-list">
							<div className="preview-setting-row">
								<span className="preview-setting-label">Simulation Type</span>
								<span className="preview-setting-value">{simulationType || "Not selected"}</span>
							</div>
							<div className="preview-setting-row">
								<span className="preview-setting-label">Attack Vector</span>
								<span className="preview-setting-value">
									<span className="template-tag-attack">{formatAttackType(attackType)}</span>
								</span>
							</div>
							<div className="preview-setting-row">
								<span className="preview-setting-label">Campaign Name</span>
								<span className="preview-setting-value">{isFollowUp ? (campaignName || "Follow-Up Campaign") : campaignName}</span>
							</div>
							{isFollowUp && followUpConditionText && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Target Condition</span>
									<span className="preview-setting-value">
										<span className="badge badge-soft-secondary">{followUpConditionText}</span>
									</span>
								</div>
							)}
							{isFollowUp && totalUsers != null && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Total Users</span>
									<span className="preview-setting-value">{totalUsers}</span>
								</div>
							)}
							{isFollowUp && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Campaign Expiry</span>
									<span className="preview-setting-value">{campaignExpiryLabel}</span>
								</div>
							)}
							{!isFollowUp && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Target Groups</span>
									<div className="preview-setting-value">
										<div className="preview-groups-container">
											{targetGroups.length > 0 ? (
												<>
													{visibleGroups.map((group, index) => (
														<span key={index} className="preview-tag preview-tag-grey">
															{group.label || group.name || group}
														</span>
													))}
													{remainingCount > 0 && (
														<span className="preview-tag preview-tag-grey">+{remainingCount}</span>
													)}
												</>
											) : (
												<span className="preview-setting-value">No groups selected</span>
											)}
										</div>
									</div>
								</div>
							)}
							{!isFollowUp && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Campaign Expiry</span>
									<span className="preview-setting-value">{campaignExpiryLabel}</span>
								</div>
							)}
							{(simulationType === "Simulation Attack + Training" && attackType !== "Attachment") && (
								<div className="preview-setting-row">
									<span className="preview-setting-label">Instant Awareness</span>
									<span className="preview-setting-value">
										{instantAwareness ? <span className="preview-tag-green">On</span> : <span className="preview-tag-red">Off</span>}
									</span>
								</div>
							)}
							{
								attackType !== "Smishing" &&
									attackType !== "Telegram" &&
									!(attackType === "Vishing" && targetInteractionType === "outbound") &&
									(attackType !== "WhatsApp" ||
										(targetInteractionType === "inbound" && isAIbased)) && (
									<div className="preview-setting-row">
										<span className="preview-setting-label">User Reply Configuration</span>
										<span className="preview-setting-value">{isRepliedCampaign ? <span className="preview-tag-green">On</span> : <span className="preview-tag-red">Off</span>}</span>
									</div>
								)
							}
							{
								attackType !== "Attachment" &&
								attackType !== "Ransomware" &&
								!(attackType === "Vishing" && targetInteractionType === "outbound") && (
									<div className="preview-setting-row">
										<span className="preview-setting-label">Credential Harvesting</span>
										<span className="preview-setting-value">{credentialHarvestingEffective
											? <span className="preview-tag-green">On</span>
											: <span className="preview-tag-red">{credentialHarvestingEnabled && noLandingPageAnywhere ? "Off \u2014 no landing page" : "Off"}</span>}</span>
									</div>
								)
							}

							<div className="preview-setting-row">
								<span className="preview-setting-label">Templates</span>
								<div className="preview-templates-grid">
									{templatesToShow.length > 0 ? (
										templatesToShow.map((template, index) => {
											const thumbnailSrc = template.thumbnailUrl
												? template.thumbnailUrl
												: simulationandtrain;

											return (
												<div key={template.id || index} className="preview-template-card">
													<div className="preview-template-image">
														<img src={thumbnailSrc} alt="Template preview" />
													</div>
													<div className="preview-template-label">{template.name || "Template Selected"}</div>
												</div>
											);
										})
									) : (
										<div className="preview-template-card">
											<div className="preview-template-image">
												<img src={simulationandtrain} alt="Template preview" />
											</div>
											<div className="preview-template-label">No template selected</div>
										</div>
									)}
								</div>
							</div>

							{simulationType === "Simulation Attack + Training" && (
								<>
									<div className="preview-setting-row">
										<span className="preview-setting-label">Training Assignment</span>
										<span className="preview-setting-value" style={{ textTransform: "capitalize" }}>
											{training?.trainingAssignmentMode || "single"}
										</span>
									</div>
									<div className="preview-setting-row">
										<span className="preview-setting-label">Training Courses</span>
										<div className="preview-setting-value">
											<div className="preview-groups-container">
												{trainingCourses.length > 0 ? (
													trainingCourses.map((courseId, index) => (
														<span key={index} className="preview-tag preview-tag-grey">
															{trainingCourseNames[courseId]?.name || `Course #${courseId}`}
														</span>
													))
												) : (
													<span className="preview-setting-value">No training courses selected</span>
												)}
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
